import type { InvitationGuestRequirements } from '@/modules/auth/use-cases'
import type { RequirementMode } from '@/shared/domain'
import { optionalFieldsOf } from '@/shared/domain'

export type JoinStepId =
  | 'personal_info'
  | 'date_of_birth'
  | 'address'
  | 'emergency_contacts'
  | 'hunting_licenses'
  | 'drivers_license'
  | 'vehicle_info'
  | 'liability_waiver'

export type JoinStep = {
  id: JoinStepId
  label: string
  mode: RequirementMode
}

function toRequirementsRecord(
  requirements: InvitationGuestRequirements,
): Record<string, unknown> {
  return requirements as unknown as Record<string, unknown>
}

function modeFor(
  requirements: InvitationGuestRequirements,
  collected: boolean,
  keys: string[],
): RequirementMode {
  if (!collected) return 'off'
  const optionalFields = optionalFieldsOf(toRequirementsRecord(requirements))
  return keys.every((key) => optionalFields.includes(key)) ? 'optional' : 'required'
}

export function computeJoinSteps(requirements: InvitationGuestRequirements): JoinStep[] {
  const steps: JoinStep[] = [
    { id: 'personal_info', label: 'Personal Info', mode: 'required' },
  ]

  const orgSteps: JoinStep[] = []

  if (requirements.basic_info?.date_of_birth?.required === true) {
    orgSteps.push({
      id: 'date_of_birth',
      label: 'Date of Birth',
      mode: modeFor(requirements, true, ['date_of_birth']),
    })
  }

  if (requirements.address === true) {
    orgSteps.push({
      id: 'address',
      label: 'Address',
      mode: modeFor(requirements, true, ['address']),
    })
  }

  const emergencyKeys = [
    ...(requirements.emergency_contact_1 === true ? ['emergency_contact_1'] : []),
    ...(requirements.emergency_contact_2 === true ? ['emergency_contact_2'] : []),
  ]
  if (emergencyKeys.length > 0) {
    orgSteps.push({
      id: 'emergency_contacts',
      label: 'Emergency Contacts',
      mode: modeFor(requirements, true, emergencyKeys),
    })
  }

  if ((requirements.required_license_types?.length ?? 0) > 0) {
    orgSteps.push({
      id: 'hunting_licenses',
      label: 'Hunting Licenses',
      mode: modeFor(requirements, true, ['licence_requirements']),
    })
  }

  if (requirements.driversLicense === true) {
    orgSteps.push({
      id: 'drivers_license',
      label: "Driver's Licence",
      mode: modeFor(requirements, true, ['drivers_license']),
    })
  }

  if (requirements.vehicle_info === true) {
    orgSteps.push({
      id: 'vehicle_info',
      label: 'Vehicle Info',
      mode: modeFor(requirements, true, ['vehicle_info']),
    })
  }

  steps.push(...orgSteps)

  if (requirements.liability_waiver === true) {
    steps.push({
      id: 'liability_waiver',
      label: 'Liability Waiver',
      mode: modeFor(requirements, true, ['liability_waiver']),
    })
  }

  return steps
}
